import { AppStoreBase } from './AppStoreBase';
import { IAppComponents } from '../models/IAppComponents';
import { computed, observable, action } from 'mobx';
import { StoreContext } from '../../../configuration/StoreContext';
import { Manifest } from '../../../configuration/Manifest';
import { IEntity, IBatchPromiseElement } from '../../../models/commonTypes';
import { utils } from '@kurtosys/ksys-app-template';
import { QueryStore } from './QueryStore';
import { ICarouselProps } from '@kurtosys/ksys-app-components/dist/components/base/Carousel/models';
import { IHeadingProps } from '@kurtosys/ksys-app-components/dist/components/base/Heading/models';
import { IRedirectButtonProps } from '@kurtosys/ksys-app-components/dist/components/base/RedirectButton/models';
import { IFundCardProps } from '../../FundCard/models';
import { FundCardStore } from '../../FundCard/stores/FundCardStore';
import { IConfigurationDataEntityMapping } from '../../../models/app/IConfigurationDataEntityMapping';
import { IConfigurationData } from '../../../models/app/IConfigurationData';

/* [Component: appStoreComponentImport] */

export class AppStore extends AppStoreBase {
	@observable.ref entitiesForCards: IEntity[] = [];
	@observable.ref childEntitiesByParentClientCode: { [key: string]: IEntity[] } = {};

	constructor(element: HTMLElement, url: string, storeContext: StoreContext, manifest: Manifest) {
		super(element, url, storeContext, manifest);
	}

	async customInitializeAfter() {
		await this.getEntitiesForCards();
	}

	@action
	async getEntitiesForCards(): Promise<void> {
		this.entitiesForCards = [];
		const { queryStore } = this.storeContext;
		const { inputs } = this.appParamsHelper;
		if (inputs) {
			const response = await queryStore.entitiesRequest(this.entityMapping.rootQueryId, inputs);
			if (response && response.values) {
				const childEntitiesByParentClientCode: { [key: string]: IEntity[] } = {};
				const batchPromises: IBatchPromiseElement[] = [];
				for (const entity of response.values) {
					const { clientCode } = entity;
					const batchPromise: IBatchPromiseElement = {
						identifier: clientCode,
						promiseFunc: () => this.getChildEntities(entity),
						responseFunc: (childEntities: IEntity[], identifier: string) => {
							if (childEntities && childEntities.length > 0) {
								childEntitiesByParentClientCode[identifier] = childEntities;
							}
						},
					};
					batchPromises.push(batchPromise);
				}
				await utils.promise.batchExecutePromises(batchPromises);
				this.entitiesForCards = response.values;
				this.childEntitiesByParentClientCode = childEntitiesByParentClientCode;
			}
		}
	}

	@action
	async getChildEntities(entity: IEntity): Promise<IEntity[]> {
		let childEntities: IEntity[] = [];
		const entityMapping = this.entityMapping;
		if (entityMapping && entityMapping.childQueryId && entityMapping.childEntityType) {
			const { rootEntityType, childQueryId } = entityMapping;
			const queryStore = new QueryStore(this.storeContext);
			queryStore.context = {
				entityByType: {
					[rootEntityType]: entity,
				},
			};
			const { appStore } = this.storeContext;
			const { appParamsHelper } = appStore;
			const { inputs } = appParamsHelper;
			if (inputs) {
				const response = await queryStore.entitiesRequest(childQueryId, inputs);
				if (response && response.values) {
					childEntities = response.values;
				}
			}
		}
		return childEntities;
	}

	@computed
	get hasData(): boolean {
		return Array.isArray(this.fundCards) && this.fundCards.length > 0;
	}

	@computed
	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	@computed
	get carouselProps(): ICarouselProps | undefined {
		return this.appComponentConfiguration && this.appComponentConfiguration.carouselProps;
	}

	@computed
	get headingProps(): IHeadingProps | undefined {
		return this.appComponentConfiguration && this.appComponentConfiguration.headingProps;
	}

	@computed
	get globalRedirectButtonProps(): IRedirectButtonProps | undefined {
		return this.appComponentConfiguration && this.appComponentConfiguration.globalRedirectButtonProps;
	}

	@computed
	get fundCards(): IFundCardProps[] {
		const response: IFundCardProps[] = [];
		for (const entity of this.entitiesForCards) {
			const { clientCode } = entity;
			response.push({
				fundCardStore: new FundCardStore(this.storeContext, entity, this.childEntitiesByParentClientCode[clientCode]),
			});
		}
		return response;
	}

	@computed
	get entityMapping(): IConfigurationDataEntityMapping {
		const dataConfiguration = this.dataConfiguration;
		if (dataConfiguration && dataConfiguration.entityMapping) {
			return dataConfiguration.entityMapping;
		}

		return {
			rootQueryId: 'entitiesForCards',
			rootEntityType: 'FUND',
		};
	}

	@computed
	get dataConfiguration(): IConfigurationData | undefined {
		return this.configuration && this.configuration.data;
	}

}